import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Program, Enrollment, getProgramTypeIcon } from '../types/program';
import { BrandBadge } from '../components/BrandBadge';
import { useBrandScopedPage } from '../hooks/useBrandScopedPage';
import { useLandingBrand } from '../hooks/useLandingBrand';
import { formatDateDisplay } from '../utils/date';
import { Modal } from '../components/Modal';
import { trackEvent } from '../utils/analytics';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import { useStatusLabel, useLabels } from '../config/useLabels';
import { SUMMER_CAMP_POLICY, isWithinCampNoRefundWindow, isSummerCampProgram } from '../utils/summerCampPolicy';
import { FlexPlanManager } from '../components/FlexPlanManager';
import { ProgramScheduleSummary } from '../components/ProgramScheduleSummary';
import { useCartHeldInvoiceIds } from '../hooks/useCartHeldInvoiceIds';
import { isPaymentInFlight } from '../utils/cart';

interface Student {
  first_name: string;
  last_name: string;
  date_of_birth: string | null;
}

interface Invoice {
  id: string;
  enrollment_id: string;
  total_amount: number;
  amount_paid: number;
  status: 'pending' | 'partial' | 'paid' | 'overdue' | 'voided';
  due_date: string;
  period_start: string;
  period_end: string;
  memo_code?: string;
  created_at: string;
  updated_at: string;
  payments: Payment[];
}

interface Payment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  status: string;
}

// Shape of the program's pricing rows as embedded by the enrollment query. PostgREST returns
// the joined config as an object or a single-element array depending on how it resolves the
// relationship, so both are handled at the read site.
interface LineItemConfigEmbed {
  name: string | null;
  type: string | null;
  subtype: string | null;
}

interface PricingEmbedRow {
  line_item_configs: LineItemConfigEmbed | LineItemConfigEmbed[] | null;
}

interface EnrollmentWithDetails extends Enrollment {
  students: Student;
  programs: Program;
  invoices: Invoice[];
  enrolled_by?: string;
  opt_in_items?: string[] | null;
  selected_days?: string[];
  signed_waiver?: { name?: string; date?: string };
  camp_lunch_selected?: boolean;
  programOffersLunch?: boolean;
}

function getStatusColor(status: string) {
  switch (status.toLowerCase()) {
    case 'paid': return 'bg-green-100 text-green-800';
    case 'partial': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'overdue': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
}

export default function EnrollmentDetails() {
  // Invoices a bank bundle payment is still debiting: no payments row yet, but money is on its way.
  const cartHeldInvoiceIds = useCartHeldInvoiceIds();
  const { enrollment_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const statusLabel = useStatusLabel();
  const t = useLabels();
  const queryClient = useQueryClient();
  const [isUnenrollModalOpen, setIsUnenrollModalOpen] = useState(false);

  const { data: enrollment, isLoading, error } = useQuery<EnrollmentWithDetails | null>({
    queryKey: ['enrollment-details', enrollment_id],
    queryFn: async () => {
      if (!enrollment_id) return null;
      const { data, error } = await supabase
        .from('enrollments')
        .select(`
          id,
          student_id,
          program_id,
          enrollment_start_date,
          created_at,
          updated_at,
          enrolled_by,
          metadata,
          is_active,
          students!inner (
            first_name,
            last_name,
            date_of_birth
          ),
          programs!inner (
            id,
            name,
            type,
            tags,
            price,
            billing_type,
            brand_key,
            active,
            start_date,
            end_date,
            schedule_cron,
            duration,
            excluded_dates,
            created_at,
            updated_at,
            pricing (
              line_item_configs:line_item_config_id (
                name,
                type,
                subtype
              )
            )
          ),
          invoices!inner (
            id,
            total_amount,
            amount_paid,
            status,
            due_date,
            period_start,
            period_end,
            is_prorated,
            created_at,
            payments (
              id,
              amount,
              payment_method,
              payment_date,
              status
            )
          )
        `)
        .eq('id', enrollment_id)
        .single();
      
      if (error) {
        if (error.code === '406' || error.code === 'PGRST116') {
          throw new Error('ENROLLMENT_NOT_FOUND');
        } else if (error.code === '42501') {
          throw new Error('PERMISSION_DENIED');
        }
        throw error;
      }
      const row = data as unknown as Record<string, unknown>;
      const md = (row.metadata as Record<string, unknown> | undefined) || {};
      const selectedDays = Array.isArray((md as Record<string, unknown>).selected_days)
        ? (md as Record<string, unknown>).selected_days as string[]
        : [];
      const optInItems = Array.isArray((md as Record<string, unknown>).opt_in_items)
        ? (md as Record<string, unknown>).opt_in_items as string[]
        : null;
      const sw = (md as Record<string, unknown>).signed_waiver as { name?: string; date?: string } | undefined;
      const campLunchSelected = typeof (md as Record<string, unknown>).camp_lunch_selected === 'boolean'
        ? (md as Record<string, unknown>).camp_lunch_selected as boolean
        : undefined;
      // Whether this program sells lunch at all, read from its pricing rather than trusted
      // from the enrollment's own metadata: a stale `camp_lunch_selected: false` would
      // otherwise tell the parent they declined an option the camp never offered.
      const programRow = row.programs as { pricing?: PricingEmbedRow[] } | undefined;
      const programOffersLunch = (programRow?.pricing ?? []).some((priceRow) => {
        const config = Array.isArray(priceRow.line_item_configs)
          ? priceRow.line_item_configs[0]
          : priceRow.line_item_configs;
        if (!config) return false;
        return [config.name, config.subtype, config.type]
          .some((field) => (field ?? '').toLowerCase().includes('lunch'));
      });
      return {
        ...row,
        selected_days: selectedDays,
        opt_in_items: optInItems,
        signed_waiver: sw,
        camp_lunch_selected: campLunchSelected,
        programOffersLunch
      } as unknown as EnrollmentWithDetails;
    },
    enabled: !!enrollment_id,
  });

  // An enrollment inherits its program's brand. Switching profiles while reading one would
  // leave the other brand's class under this brand's header, so the parent is sent home.
  // There is no enrollment list to return to — the dashboard is the closest thing, and it is
  // already brand-scoped, so it shows the profile they just switched into.
  useBrandScopedPage(enrollment?.programs?.brand_key, '/dashboard');
  // And the other direction: a link into this enrollment from outside opens on its brand.
  useLandingBrand(enrollment?.programs?.brand_key);

  // Get current parent's ID for authorization checks
  const { data: currentParent } = useQuery({
    queryKey: ['current-parent'],
    queryFn: async () => {
      if (!user?.email) return null;
      const { data, error } = await supabase
        .from('parents')
        .select('id')
        .eq('email', user.email)
        .single();
      if (error) return null;
      return data;
    },
    enabled: !!user?.email,
  });

  const unenrollMutation = useMutation({
    mutationFn: async (enrollmentId: string) => {
      const { error } = await supabase
        .from('enrollments')
        .delete()
        .eq('id', enrollmentId);

      if (error) {
        // Provide more specific error messages based on the error
        if (error.code === '42501') {
          throw new Error('You do not have permission to unenroll from this program. Only the parent who enrolled the student can unenroll them.');
        } else if (error.code === '23503') {
          throw new Error('Cannot unenroll: This enrollment has associated payments or invoices that prevent deletion.');
        } else {
          throw new Error(`Error unenrolling: ${error.message}`);
        }
      }
    },
    onSuccess: () => {
      if (enrollment && user) {
        const studentName = `${enrollment.students?.first_name} ${enrollment.students?.last_name}`;
        // The unenroll actually committed — log a distinct completion event so
        // analytics no longer conflate "opened the confirm dialog" with a real
        // unenroll (see program_unenroll_start in handleUnenroll).
        trackEvent({
          eventType: 'program_unenroll_complete',
          user: user,
          entityType: 'enrollment',
          entityId: enrollment.id,
          metadata: {
            program_id: enrollment.program_id,
            program_name: enrollment.programs?.name,
            // The row is deleted by the time anything reads this, so student_id has to
            // travel on the event -- the admin overview's "students with enrollment"
            // delta has nothing left to join to.
            student_id: enrollment.student_id,
            student_name: studentName,
          },
          email: user.email,
        });

        // Warn admins: a parent self-unenrolled a student. Fire-and-forget so a
        // notification failure never blocks the redirect. The enrollment row is
        // already deleted, so all display fields are passed from the client here.
        void supabase.functions
          .invoke('notify-unenroll', {
            body: {
              enrollmentId: enrollment.id,
              programId: enrollment.program_id,
              programName: enrollment.programs?.name,
              studentName,
              startDate: enrollment.enrollment_start_date,
            },
          })
          .then(({ error }) => {
            if (error) console.error('Failed to send unenroll admin notification:', error);
          })
          .catch((e) => console.error('Failed to send unenroll admin notification:', e));
      }

      // Invalidate the enrollment query so other users on the page get updated
      queryClient.invalidateQueries({ queryKey: ['enrollment-details', enrollment_id] });
      navigate('/dashboard');
    },
    onError: (err: Error) => {
      if (enrollment && user) {
        trackEvent({
          eventType: 'program_unenroll_failed',
          user: user,
          entityType: 'enrollment',
          entityId: enrollment.id,
          success: false,
          errorMessage: err.message,
          metadata: {
            program_id: enrollment.program_id,
            program_name: enrollment.programs?.name,
            student_name: `${enrollment.students?.first_name} ${enrollment.students?.last_name}`,
          },
          email: user.email,
        });
      }
      alert(err.message);
    },
  });

  const handleUnenroll = () => {
    if (enrollment && user) {
      // Fired when the parent opens the confirmation dialog — this is intent to
      // unenroll, not a completed unenroll. The matching completion/failure is
      // tracked in the mutation's onSuccess/onError.
      trackEvent({
        eventType: 'program_unenroll_start',
        user: user,
        entityType: 'enrollment',
        entityId: enrollment.id,
        metadata: {
          program_id: enrollment.program_id,
          program_name: enrollment.programs?.name,
          student_name: `${enrollment.students?.first_name} ${enrollment.students?.last_name}`,
        },
        email: user.email,
      });
    }
    setIsUnenrollModalOpen(true);
  };

  const confirmUnenroll = () => {
    if (enrollment) {
      unenrollMutation.mutate(enrollment.id);
    }
    setIsUnenrollModalOpen(false);
  };

  const canUnenroll = useMemo(() => {
    if (!enrollment) {
      return false;
    }

    // Authorization check: only allow if current parent is the one who enrolled
    if (!currentParent || enrollment.enrolled_by !== currentParent.id) {
      return false;
    }

    // Parse enrollment start date consistently using UTC to avoid timezone issues
    if (!enrollment.enrollment_start_date) {
      return false;
    }
    
    const enrollmentStartDate = new Date(
      parseInt(enrollment.enrollment_start_date.split('-')[0]),
      parseInt(enrollment.enrollment_start_date.split('-')[1]) - 1,
      parseInt(enrollment.enrollment_start_date.split('-')[2])
    );
    
    // Get current date in the same timezone format
    const now = new Date();
    const todayLocal = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    if (enrollmentStartDate <= todayLocal) {
      return false;
    }

    const totalPaid = enrollment.invoices?.reduce((sum, inv) => sum + inv.amount_paid, 0) || 0;
    if (totalPaid > 0) {
      return false;
    }

    // Check if any invoice has processing payments (covers 'pending' and 'partial'
    // invoice status — the latter occurs after an ACH+BHCS Cash split pay)
    const hasProcessingPayments = enrollment.invoices?.some(invoice => isPaymentInFlight(invoice, cartHeldInvoiceIds)) || false;
    
    if (hasProcessingPayments) {
      return false;
    }

    if (isSummerCampProgram(enrollment.programs) && isWithinCampNoRefundWindow(enrollment.enrollment_start_date)) {
      return false;
    }

    return true;
  }, [enrollment, currentParent]);

  // Fetch line item names for opted-out items
  const { data: lineItemConfigs } = useQuery({
    queryKey: ['line-item-configs', enrollment?.opt_in_items],
    queryFn: async () => {
      if (!enrollment?.opt_in_items?.length) return [];
      const { data } = await supabase
        .from('line_item_configs')
        .select('id, name')
        .in('id', enrollment.opt_in_items);
      return data || [];
    },
    enabled: !!enrollment?.opt_in_items?.length,
  });

  if (isLoading) {
    return <div>Loading enrollment details...</div>;
  }

  if (error || !enrollment) {
    const isEnrollmentNotFound = error?.message === 'ENROLLMENT_NOT_FOUND';
    const isPermissionDenied = error?.message === 'PERMISSION_DENIED';
    
    return (
      <div className="p-3 sm:p-4">
        <button onClick={() => navigate('/dashboard')} className="mb-4 text-blue-600 hover:underline">
          &larr; {t('action.backToDashboard')}
        </button>
        <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">{t('header.enrollmentDetails')}</h1>
        <div className="bg-white p-6 rounded-lg shadow">
          {isEnrollmentNotFound ? (
            <div className="text-center">
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                This enrollment could not be found. It may have been removed or you may not have permission to view it.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          ) : isPermissionDenied ? (
            <div className="text-center">
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                You don't have permission to view this enrollment. Only the parent who enrolled the student can view the enrollment details.
              </p>
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Return to Dashboard
              </button>
            </div>
          ) : (
            <p className="text-sm sm:text-base text-red-600">Error loading enrollment. Please try again later.</p>
          )}
        </div>
      </div>
    );
  }

  const isCampWithUnpaidInvoice = enrollment.programs?.type === 'camp' &&
    enrollment.invoices?.some(inv => {
      if (!['pending', 'partial', 'overdue'].includes(inv.status)) return false;
      const hasInflightAch = inv.payments?.some(
        p => p.payment_method === 'ach' && ['processing', 'pending', 'created'].includes(p.status.toLowerCase())
      ) ?? false;
      return !hasInflightAch && (inv.total_amount - inv.amount_paid) > 0;
    });

  const unpaidCampInvoices = isCampWithUnpaidInvoice
    ? (enrollment.invoices?.filter(inv => {
        if (!['pending', 'partial', 'overdue'].includes(inv.status)) return false;
        const hasInflightAch = inv.payments?.some(
          p => p.payment_method === 'ach' && ['processing', 'pending', 'created'].includes(p.status.toLowerCase())
        ) ?? false;
        return !hasInflightAch && (inv.total_amount - inv.amount_paid) > 0;
      }) ?? [])
    : [];
  const isCampEnrollmentWithPolicy = isSummerCampProgram(enrollment.programs);
  const isCampInNoRefundWindow = isCampEnrollmentWithPolicy && isWithinCampNoRefundWindow(enrollment.enrollment_start_date);

  return (
    <div className="p-3 sm:p-4">
      <button onClick={() => navigate('/dashboard')} className="mb-4 text-blue-600 hover:underline">&larr; {t('action.backToDashboard')}</button>
      <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-6">{t('header.enrollmentDetails')}</h1>

      {isCampWithUnpaidInvoice && unpaidCampInvoices.length > 0 && (
        <div
          className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          role="alert"
          data-testid="camp-payment-banner"
        >
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-800">
              Enrollment not complete — payment required
            </p>
            <p className="text-sm text-amber-700 mt-0.5">
              Your enrollment request for <span className="font-medium">{enrollment.programs?.name}</span> is
              received. It is complete once full payment is received.
            </p>
          </div>
          <div className="flex-shrink-0 space-y-2">
            {unpaidCampInvoices.map((invoice, index) => (
              <Link
                key={invoice.id}
                to={`/payment-checkout/${invoice.id}`}
                className="block text-center px-4 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                data-testid={index === 0 ? 'camp-banner-pay-now' : `camp-banner-pay-now-${invoice.id}`}
              >
                Pay Now
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 sm:p-6 border-b">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              {enrollment.programs?.type && getProgramTypeIcon(enrollment.programs.type)}
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                  {(() => {
                    const lvlUnknown = (enrollment.metadata as Record<string, unknown> | undefined)?.level;
                    const lvl = typeof lvlUnknown === 'string' && lvlUnknown.trim() ? lvlUnknown : '';
                    return lvl ? `${enrollment.programs?.name} (${lvl})` : enrollment.programs?.name;
                  })()}
                </h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <BrandBadge brand={enrollment.programs?.brand_key} size="xs" />
                  <span className="text-sm text-gray-700">
                    {enrollment.students?.first_name} {enrollment.students?.last_name}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    enrollment.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {enrollment.is_active ? t('status.active') : t('status.inactive')}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                type="button"
                onClick={() => navigate(`/programs/${enrollment.programs.id}`)}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-xs sm:text-sm"
                title="View Program Details"
              >
                {t('action.viewProgram')}
              </button>
            </div>
          </div>
          {enrollment.signed_waiver && (enrollment.signed_waiver.name || enrollment.signed_waiver.date) && (
            <div className="mt-2">
              <p className="text-xs text-gray-500">
                Waiver acknowledged{enrollment.signed_waiver.name ? ` by ${enrollment.signed_waiver.name}` : ''}
                {enrollment.signed_waiver.date ? ` on ${formatDateDisplay(enrollment.signed_waiver.date)}` : ''}.
              </p>
            </div>
          )}
          {isCampEnrollmentWithPolicy && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="camp-policy-enrollment-details">
              <p className="text-sm font-semibold text-amber-900">Summer camp cancellation and refund policy</p>
              <ul className="mt-2 list-disc pl-5 text-sm text-amber-800 space-y-1">
                {SUMMER_CAMP_POLICY.cancellationTiers.map((tier) => (
                  <li key={tier}>{tier}</li>
                ))}
              </ul>
              <p className="mt-2 text-xs text-amber-700">
                Questions or requests: <span className="font-medium">{SUMMER_CAMP_POLICY.supportEmail}</span>
              </p>
              {isCampInNoRefundWindow && (
                <p className="mt-2 text-xs font-semibold text-red-700">
                  This enrollment is within 2 weeks of camp start. Self-service unenroll is disabled; contact billing for operational support.
                </p>
              )}
            </div>
          )}
          {/* The same block the program page shows, so a family that has already enrolled can
              still answer "when does this meet and which days are off" without navigating back
              to the program and reading a description that may disagree. */}
          <ProgramScheduleSummary
            program={enrollment.programs}
            termStart={enrollment.enrollment_start_date}
            className="mt-6"
            extraPills={
              /* "Included" records what the family bought and stays true regardless of later
                 pricing changes. "Not included" is the ambiguous one — it means "you declined
                 a real option" only if the program sells lunch — so it is gated on the
                 program's own pricing. */
              (enrollment.camp_lunch_selected === true
                || (enrollment.camp_lunch_selected === false && enrollment.programOffersLunch)) ? (
                <span className={`inline-flex items-center text-xs rounded-full px-2 py-0.5 font-medium ${enrollment.camp_lunch_selected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {enrollment.camp_lunch_selected ? t('label.lunchIncluded') : t('label.lunchNotIncluded')}
                </span>
              ) : null
            }
          />
          {enrollment.programs?.billing_type === 'per_day' && enrollment.selected_days && enrollment.selected_days.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Selected Dates ({enrollment.selected_days.length} days)</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {enrollment.selected_days.map((date, index) => (
                  <div key={index} className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs sm:text-sm font-medium text-center">
                    {formatDateDisplay(date)}
                  </div>
                ))}
              </div>
            </div>
          )}
          {enrollment.programs?.billing_type === 'flex_monthly' && enrollment.is_active && (
            <FlexPlanManager
              enrollmentId={enrollment.id}
              programId={enrollment.programs.id}
              brandKey={enrollment.programs.brand_key}
              scheduleCron={enrollment.programs.schedule_cron}
              programEndDate={enrollment.programs.end_date}
              enrollmentStartDate={enrollment.enrollment_start_date}
              optInItems={enrollment.opt_in_items ?? null}
            />
          )}
          {enrollment.opt_in_items && enrollment.opt_in_items.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Optional Items Included</h3>
              <div className="flex flex-wrap gap-2">
                {enrollment.opt_in_items.map((itemId) => {
                  const itemConfig = lineItemConfigs?.find(config => config.id === itemId);
                  return (
                    <span key={itemId} className="px-2 py-1 bg-gray-200 text-gray-700 rounded-full text-xs sm:text-sm">
                      {itemConfig?.name || itemId}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex items-center mt-4 space-x-4">
            {canUnenroll && (
              <button
                type="button"
                onClick={handleUnenroll}
                disabled={unenrollMutation.isPending}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-50"
                title="Unenroll from this program"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                <span>{unenrollMutation.isPending ? 'Processing...' : t('action.unenroll')}</span>
              </button>
            )}
          </div>
        </div>
                  <div className="p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4">{t('tab.invoices')}</h3>
          {enrollment.invoices && enrollment.invoices.length > 0 ? (
            <div className="space-y-3">
              {enrollment.invoices
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((invoice) => {
                const isProrated = Boolean((invoice as { is_prorated?: boolean }).is_prorated);

                const isPaymentProcessing = isPaymentInFlight(invoice, cartHeldInvoiceIds);

                return (
                  <div
                    key={invoice.id}
                    className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4 cursor-pointer"
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-900 truncate max-w-xs">
                          Invoice #{invoice.id}
                        </p>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${isPaymentProcessing ? 'bg-blue-100 text-blue-800' : getStatusColor(invoice.status)}`}>
                          {statusLabel(isPaymentProcessing ? 'payment_processing' : invoice.status)}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                          Due {formatDateDisplay(invoice.due_date)}
                        </span>
                        <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">
                          {formatDateDisplay(invoice.period_start)} – {formatDateDisplay(invoice.period_end)}
                        </span>
                      </div>
                      {isProrated && (
                        <p className="text-xs text-yellow-700 mt-1 font-medium">
                          ℹ️ Prorated billing - view invoice details for full breakdown
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 md:gap-3">
                      <span className="text-sm font-medium text-gray-900">
                        ${invoice.amount_paid.toFixed(2)} (paid)/ ${invoice.total_amount.toFixed(2)} (total)
                      </span>
                      <button
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/invoices/${invoice.id}`);
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        {t('action.viewInvoice')}
                      </button>
                      {invoice.status !== 'paid' && invoice.status !== 'voided' && !isPaymentProcessing && (
                        <button
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-semibold flex items-center gap-2 shadow-sm transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/payment-checkout/${invoice.id}`);
                          }}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                          Make Payment
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs sm:text-sm text-gray-500">No invoices found</p>
          )}
        </div>
      </div>
      <Modal
        isOpen={isUnenrollModalOpen}
        onClose={() => setIsUnenrollModalOpen(false)}
        title="Confirm Unenrollment"
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">
            {isCampEnrollmentWithPolicy
              ? `Are you sure you want to unenroll from this camp program? This will delete the enrollment and any associated invoices. Refund eligibility follows the posted camp policy. If self-service is unavailable, contact ${SUMMER_CAMP_POLICY.supportEmail}.`
              : 'Are you sure you want to unenroll from this program? This will delete the enrollment and any associated invoices. This action cannot be undone.'}
          </p>
          <div className="flex justify-end space-x-2">
            <button
              onClick={() => setIsUnenrollModalOpen(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400"
            >
              Cancel
            </button>
            <button
              onClick={confirmUnenroll}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Confirm Unenroll
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
} 